import PropTypes from 'prop-types';
import React from 'react';
import { Button, TableCell, TableRow, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { isBoolean, isNil } from 'lodash';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { SigStatus } from 'enums/SigManagement.enum';

export const SIG_ROW_COLUMN_COUNT = 7;

const EM_DASH = '—';

const formatDate = (value) =>
  value ? format(utcToZonedTime(new Date(value), 'utc'), 'MM/dd/yyyy') : 'N/A';

const sigStatusLabel = (sigStatus) => {
  if (!isBoolean(sigStatus)) {
    return 'N/A';
  }

  return sigStatus ? SigStatus.ACTIVE : SigStatus.INACTIVE;
};

/**
 * One sig, as counts.
 *
 * The row used to expand into the sig's directions and drug approvals, which meant the list query
 * carried every direction row for every sig on the page. It carries two numbers now, and opening
 * the sig fetches the rows behind them.
 */
const SigRow = ({ sigRow, onOpenDetail }) => {
  const { sigId, sig, sigStatus, createdAt, updatedAt, directionCount, approvedDrugCount } = sigRow;

  return (
    <TableRow hover onClick={onOpenDetail} sx={{ cursor: 'pointer' }}>
      <TableCell align='left' sx={{ maxWidth: '360px' }}>
        <Typography variant='body2'>{sig}</Typography>
      </TableCell>

      {/* A dash rather than a 0: an absent count means the field did not arrive, and a 0 would
          claim the sig has nothing linked to it. */}
      <TableCell align='center'>{isNil(directionCount) ? EM_DASH : directionCount}</TableCell>
      <TableCell align='center'>{isNil(approvedDrugCount) ? EM_DASH : approvedDrugCount}</TableCell>

      <TableCell align='left'>{formatDate(createdAt)}</TableCell>
      <TableCell align='left'>{formatDate(updatedAt)}</TableCell>

      <TableCell align='left'>{sigStatusLabel(sigStatus)}</TableCell>

      {/* Named, not a chevron. The chevron was left over from when the row expanded into the sig's
          directions in place: it described a behaviour the row no longer has, and read as "expand"
          or "next" rather than "open the screen that manages this sig's associations".

          Outlined and small on purpose, so it stays a row action. Contained would put twenty-five
          filled buttons on the page competing with Add New, which is the one primary action here.

          The accessible name carries the sig even though the visible label does not: twenty-five
          buttons all announcing "Manage Association" tell a screen reader user nothing about which
          row they are on. */}
      <TableCell align='right'>
        <Button
          id={`Sig-${sigId}-detail-button`}
          variant='outlined'
          size='small'
          startIcon={<TuneIcon />}
          aria-label={`Manage association: ${sig}`}
          onClick={(event) => {
            // The row opens the dialog too; without this the dialog would open twice.
            event.stopPropagation();
            onOpenDetail();
          }}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Manage Association
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default SigRow;

SigRow.propTypes = {
  sigRow: PropTypes.shape({
    sigId: PropTypes.string.isRequired,
    sig: PropTypes.string.isRequired,
    sigStatus: PropTypes.bool,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    directionCount: PropTypes.number,
    approvedDrugCount: PropTypes.number,
  }).isRequired,
  onOpenDetail: PropTypes.func.isRequired,
};
